/**
 * BauxPanel — Module Baux légaux Québec + Signature électronique.
 * Rattaché à un immeuble : liste des baux, création/édition, génération PDF,
 * envoi du lien de signature au locataire, signature du locateur (canvas).
 */
import { useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { FileText, Plus, Trash, PaperPlaneTilt, FilePdf, PencilSimple, Signature, X, CheckCircle, Clock } from "@phosphor-icons/react";

const STATUT_BADGE = {
  brouillon: { label: "Brouillon", cls: "bg-zinc-700 text-zinc-200" },
  "envoyé": { label: "Envoyé", cls: "bg-blue-900 text-blue-200" },
  "signé": { label: "Signé", cls: "bg-green-900 text-green-200" },
};

const SERVICES = [
  ["chauffage", "Chauffage"], ["electricite", "Électricité"], ["eau_chaude", "Eau chaude"],
  ["chauffe_eau", "Chauffe-eau"], ["gaz", "Gaz"], ["deneigement", "Déneigement"],
  ["stationnement", "Stationnement"], ["rangement", "Rangement"],
];

function Field({ label, value, onChange, type = "text", testId }) {
  return (
    <label className="block">
      <span className="text-xs text-zinc-400">{label}</span>
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="mt-1 w-full bg-zinc-800 border border-zinc-700 rounded px-2.5 py-1.5 text-sm text-zinc-100 focus:border-amber-500 outline-none"
      />
    </label>
  );
}

function SignatureModal({ onClose, onSave }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });
  const [sig, setSig] = useState("");

  const pos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - r.left, y: t.clientY - r.top };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; last.current = pos(e);
    const ctx = canvasRef.current.getContext("2d"); ctx.lineWidth = 2.5; ctx.lineCap = "round"; ctx.strokeStyle = "#111"; };
  const move = (e) => {
    if (!drawing.current) return; e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = pos(e); ctx.beginPath(); ctx.moveTo(last.current.x, last.current.y); ctx.lineTo(p.x, p.y); ctx.stroke();
    last.current = p; setSig(canvasRef.current.toDataURL("image/png"));
  };
  const end = () => { drawing.current = false; };
  const clear = () => { const c = canvasRef.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); setSig(""); };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-5 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-white font-semibold">Signer comme locateur</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-200"><X size={18} /></button>
        </div>
        <canvas
          ref={canvasRef} width={420} height={160} data-testid="locateur-signature-canvas"
          className="w-full border-2 border-dashed border-zinc-600 rounded-lg bg-white touch-none cursor-crosshair"
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        />
        <div className="flex justify-between mt-3">
          <button onClick={clear} className="text-xs text-zinc-400 hover:text-red-400 underline">Effacer</button>
          <button
            onClick={() => { if (!sig) return toast.error("Signez d'abord."); onSave(sig); }}
            data-testid="save-locateur-signature-btn"
            className="bg-amber-500 hover:bg-amber-600 text-zinc-950 text-sm font-semibold px-4 py-1.5 rounded-lg"
          >Confirmer</button>
        </div>
      </div>
    </div>
  );
}

function BailEditor({ immeubleId, portes, bail, onClose, onSaved }) {
  const [form, setForm] = useState(() => bail || {
    porte_id: portes?.[0]?.id || "",
    locateur: { nom: "Ralph David Jean-Pierre", adresse: "22 Chemin Simon-Robitaille", ville: "Val-d'Or", telephone: "(819) 527-8489", courriel: "ralph646@hotmail.com" },
    locataire: { nom: "", adresse: "", ville: "Val-d'Or", code_postal: "", telephone: "", courriel: "" },
    logement: { adresse: "", ville: "Val-d'Or", code_postal: "", app: "", nombre_pieces: "" },
    bail: { type: "fixe", duree: "1 an", date_debut: "", date_fin: "" },
    loyer: { montant: "", frequence: "mois", jour_paiement: "1er jour du mois", mode_paiement: "Virement bancaire électronique", lieu_paiement: "Bureau du locateur" },
    services: {}, conditions: { animaux: "non", stationnement: "non", fumeur: "non" }, clauses_speciales: "",
  });
  const [saving, setSaving] = useState(false);
  const set = (section, key, val) => setForm((f) => ({ ...f, [section]: { ...f[section], [key]: val } }));

  async function save() {
    setSaving(true);
    try {
      if (bail?.id) {
        await api.put(`/immeubles/baux/${bail.id}`, form);
      } else {
        await api.post(`/immeubles/${immeubleId}/baux`, form);
      }
      toast.success("Bail enregistré");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'enregistrement");
    }
    setSaving(false);
  }

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-5 max-w-2xl w-full my-8 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="bail-editor">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-white font-semibold">{bail?.id ? "Modifier le bail" : "Nouveau bail"}</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-200"><X size={20} /></button>
        </div>

        <div className="space-y-5">
          <div>
            <div className="text-xs uppercase tracking-wide text-amber-500 mb-2">Porte</div>
            <select value={form.porte_id || ""} onChange={(e) => setForm((f) => ({ ...f, porte_id: e.target.value }))}
              data-testid="bail-porte-select"
              className="w-full bg-zinc-800 border border-zinc-700 rounded px-2.5 py-1.5 text-sm text-zinc-100">
              {(portes || []).map((p) => <option key={p.id} value={p.id}>{p.identifiant} — {p.adresse}</option>)}
            </select>
          </div>

          <div>
            <div className="text-xs uppercase tracking-wide text-amber-500 mb-2">Locataire</div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Nom complet" value={form.locataire.nom} onChange={(v) => set("locataire", "nom", v)} testId="bail-locataire-nom" />
              <Field label="Courriel" value={form.locataire.courriel} onChange={(v) => set("locataire", "courriel", v)} testId="bail-locataire-courriel" />
              <Field label="Téléphone" value={form.locataire.telephone} onChange={(v) => set("locataire", "telephone", v)} />
              <Field label="Adresse actuelle" value={form.locataire.adresse} onChange={(v) => set("locataire", "adresse", v)} />
            </div>
          </div>

          <div>
            <div className="text-xs uppercase tracking-wide text-amber-500 mb-2">Logement</div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Adresse du logement" value={form.logement.adresse} onChange={(v) => set("logement", "adresse", v)} />
              <Field label="Appartement" value={form.logement.app} onChange={(v) => set("logement", "app", v)} />
              <Field label="Code postal" value={form.logement.code_postal} onChange={(v) => set("logement", "code_postal", v)} />
              <Field label="Nombre de pièces" value={form.logement.nombre_pieces} onChange={(v) => set("logement", "nombre_pieces", v)} />
            </div>
          </div>

          <div>
            <div className="text-xs uppercase tracking-wide text-amber-500 mb-2">Durée & loyer</div>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-xs text-zinc-400">Type de bail</span>
                <select value={form.bail.type} onChange={(e) => set("bail", "type", e.target.value)}
                  className="mt-1 w-full bg-zinc-800 border border-zinc-700 rounded px-2.5 py-1.5 text-sm text-zinc-100">
                  <option value="fixe">Durée fixe</option>
                  <option value="indetermine">Durée indéterminée</option>
                </select>
              </label>
              <Field label="Durée" value={form.bail.duree} onChange={(v) => set("bail", "duree", v)} />
              <Field label="Date de début" value={form.bail.date_debut} onChange={(v) => set("bail", "date_debut", v)} type="date" testId="bail-date-debut" />
              <Field label="Date de fin" value={form.bail.date_fin} onChange={(v) => set("bail", "date_fin", v)} type="date" testId="bail-date-fin" />
              <Field label="Loyer ($)" value={form.loyer.montant} onChange={(v) => set("loyer", "montant", v)} type="number" testId="bail-loyer" />
              <Field label="Jour de paiement" value={form.loyer.jour_paiement} onChange={(v) => set("loyer", "jour_paiement", v)} />
            </div>
          </div>

          <div>
            <div className="text-xs uppercase tracking-wide text-amber-500 mb-2">Services inclus (à la charge du locateur)</div>
            <div className="flex flex-wrap gap-2">
              {SERVICES.map(([key, label]) => {
                const on = String(form.services?.[key] || "").toLowerCase() === "oui";
                return (
                  <button key={key} onClick={() => set("services", key, on ? "non" : "oui")}
                    className={`text-xs px-3 py-1.5 rounded-full border ${on ? "bg-amber-500/20 border-amber-500 text-amber-300" : "border-zinc-700 text-zinc-400"}`}>
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <div className="text-xs uppercase tracking-wide text-amber-500 mb-2">Clauses particulières</div>
            <textarea value={form.clauses_speciales} onChange={(e) => setForm((f) => ({ ...f, clauses_speciales: e.target.value }))}
              rows={3} data-testid="bail-clauses"
              className="w-full bg-zinc-800 border border-zinc-700 rounded px-2.5 py-1.5 text-sm text-zinc-100 focus:border-amber-500 outline-none" />
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-4 py-2 text-sm text-zinc-400 hover:text-zinc-200">Annuler</button>
          <button onClick={save} disabled={saving} data-testid="save-bail-btn"
            className="bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-zinc-950 text-sm font-semibold px-4 py-2 rounded-lg">
            {saving ? "Enregistrement…" : "Enregistrer"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function BauxPanel({ immeubleId, portes }) {
  const [baux, setBaux] = useState([]);
  const [editor, setEditor] = useState(null); // {bail} or {} for new
  const [sigModal, setSigModal] = useState(null); // bail id
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/immeubles/${immeubleId}/baux`);
      setBaux(r.data.baux || []);
    } catch { toast.error("Erreur de chargement des baux"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, [immeubleId]); // eslint-disable-line

  const pdfUrl = (id) => `${process.env.REACT_APP_BACKEND_URL}/api/immeubles/baux/${id}/pdf`;

  async function openPdf(id) {
    try {
      const r = await api.get(`/immeubles/baux/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank");
    } catch { toast.error("Échec de génération du PDF"); }
  }

  async function envoyerSignature(b) {
    if (!b.locataire?.courriel) return toast.error("Le locataire n'a pas de courriel.");
    if (!window.confirm(`Envoyer le lien de signature à ${b.locataire.nom} (${b.locataire.courriel}) ?`)) return;
    try {
      const r = await api.post(`/immeubles/baux/${b.id}/envoyer-signature`, { base_url: window.location.origin });
      toast.success(`Lien de signature envoyé à ${r.data.courriel}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'envoi (vérifie Gmail)");
    }
  }

  async function signerLocateur(sig) {
    try {
      await api.post(`/immeubles/baux/${sigModal}/signer-locateur`, { signature_data: sig });
      toast.success("Bail signé (locateur)");
      setSigModal(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de la signature");
    }
  }

  async function del(id) {
    if (!window.confirm("Supprimer ce bail ?")) return;
    try { await api.delete(`/immeubles/baux/${id}`); load(); toast.success("Bail supprimé"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Échec"); }
  }

  return (
    <div className="space-y-4" data-testid="baux-panel">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-white font-semibold flex items-center gap-2"><FileText size={18} className="text-amber-400" /> Baux légaux & signature électronique</h3>
          <p className="text-xs text-zinc-500 mt-0.5">Bail résidentiel québécois (TAL) numérisé · signature à distance horodatée.</p>
        </div>
        <button onClick={() => setEditor({})} data-testid="new-bail-btn"
          className="inline-flex items-center gap-1.5 bg-amber-500 hover:bg-amber-600 text-zinc-950 text-sm font-semibold px-3 py-2 rounded-lg">
          <Plus size={16} weight="bold" /> Nouveau bail
        </button>
      </div>

      {loading ? (
        <div className="text-zinc-500 py-8">Chargement…</div>
      ) : baux.length === 0 ? (
        <div className="text-zinc-500 py-8 text-center border border-dashed border-zinc-800 rounded-lg" data-testid="baux-empty">
          Aucun bail. Cliquez « Nouveau bail » pour numériser un bail et le faire signer électroniquement.
        </div>
      ) : (
        <div className="space-y-3" data-testid="baux-list">
          {baux.map((b) => {
            const badge = STATUT_BADGE[b.statut] || STATUT_BADGE.brouillon;
            return (
              <div key={b.id} className="border border-zinc-800 rounded-lg p-4 bg-zinc-900/50" data-testid={`bail-card-${b.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-white font-medium">{b.locataire?.nom || "Locataire à définir"}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${badge.cls}`}>{badge.label}</span>
                    </div>
                    <div className="text-xs text-zinc-400 mt-1">
                      {b.logement?.adresse} · {b.loyer?.montant ? `${b.loyer.montant} $/mois` : "loyer —"} · {b.bail?.date_debut || "?"} → {b.bail?.date_fin || "?"}
                    </div>
                    {b.conditions_lisibles && (
                      <div className="text-[11px] text-zinc-400 mt-1" data-testid={`bail-conditions-${b.id}`}>
                        Fumer : {b.conditions_lisibles.fumer} · Stationnement : {b.conditions_lisibles.stationnement} · Animaux : {b.conditions_lisibles.animaux}
                        {b.conditions_lisibles.incoherences?.length > 0 && <div className="mt-1 text-amber-300" data-testid={`bail-incoherence-${b.id}`}>⚠️ À vérifier avant envoi : {b.conditions_lisibles.incoherences.join(" ")}</div>}
                      </div>
                    )}
                    <div className="flex items-center gap-3 mt-2 text-[11px]">
                      <span className={`flex items-center gap-1 ${b.signature_locateur_at ? "text-green-400" : "text-zinc-500"}`}>
                        {b.signature_locateur_at ? <CheckCircle size={13} weight="fill" /> : <Clock size={13} />} Locateur
                      </span>
                      <span className={`flex items-center gap-1 ${b.signature_locataire_at ? "text-green-400" : "text-zinc-500"}`}>
                        {b.signature_locataire_at ? <CheckCircle size={13} weight="fill" /> : <Clock size={13} />} Locataire
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  <button onClick={() => openPdf(b.id)} data-testid={`bail-pdf-${b.id}`}
                    className="inline-flex items-center gap-1 text-xs bg-zinc-800 hover:bg-zinc-700 text-zinc-200 px-2.5 py-1.5 rounded">
                    <FilePdf size={14} /> PDF
                  </button>
                  <button onClick={() => envoyerSignature(b)} data-testid={`bail-send-${b.id}`}
                    className="inline-flex items-center gap-1 text-xs bg-blue-900/50 hover:bg-blue-900 text-blue-200 px-2.5 py-1.5 rounded">
                    <PaperPlaneTilt size={14} /> Envoyer à signer
                  </button>
                  {!b.signature_locateur_at && (
                    <button onClick={() => setSigModal(b.id)} data-testid={`bail-sign-locateur-${b.id}`}
                      className="inline-flex items-center gap-1 text-xs bg-amber-900/40 hover:bg-amber-900 text-amber-200 px-2.5 py-1.5 rounded">
                      <Signature size={14} /> Signer (locateur)
                    </button>
                  )}
                  {b.statut !== "signé" && (
                    <button onClick={() => setEditor({ bail: b })} data-testid={`bail-edit-${b.id}`}
                      className="inline-flex items-center gap-1 text-xs bg-zinc-800 hover:bg-zinc-700 text-zinc-200 px-2.5 py-1.5 rounded">
                      <PencilSimple size={14} /> Modifier
                    </button>
                  )}
                  <button onClick={() => del(b.id)} className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-red-400 px-2.5 py-1.5">
                    <Trash size={14} /> Supprimer
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editor && (
        <BailEditor
          immeubleId={immeubleId} portes={portes} bail={editor.bail}
          onClose={() => setEditor(null)}
          onSaved={() => { setEditor(null); load(); }}
        />
      )}
      {sigModal && <SignatureModal onClose={() => setSigModal(null)} onSave={signerLocateur} />}
    </div>
  );
}
